import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { Drawer as DrawerPrimitive } from '@base-ui/react/drawer';
import { useMergedRefs } from '@base-ui/utils/useMergedRefs';
import { cva, type VariantProps } from 'class-variance-authority';
import { XIcon } from 'lucide-react';
import {
    useRef,
    type ComponentPropsWithRef,
    type ComponentType,
    type CSSProperties,
    type ReactElement,
} from 'react';
import { IconButton } from '../candidates/icon-button.tsx';
import { Button, type ButtonVariantProps } from '../primitives/button.tsx';
import { cn } from '../util/classes.ts';
import { useBreakpoint } from '../util/use-breakpoint.tsx';
import { useControllableState } from '../util/use-controllable-state.tsx';
import { Container } from './container.tsx';

type RootPrimitiveProps = Omit<DialogPrimitive.Root.Props, 'onOpenChange'> & {
    onOpenChange?: (open: boolean) => void;
};

type BackdropPrimitiveProps = Omit<DialogPrimitive.Backdrop.Props, 'className' | 'render' | 'style'> & {
    className?: string;
    style?: CSSProperties;
};

type PopupPrimitiveProps = Omit<DialogPrimitive.Popup.Props, 'className' | 'render' | 'style'> & {
    className?: string;
    style?: CSSProperties;
};

type RenderedButtonPrimitiveProps = {
    'data-slot'?: string;
    render?: ReactElement;
};

type TitlePrimitiveProps = Omit<DialogPrimitive.Title.Props, 'className' | 'render' | 'style'> & {
    className?: string;
    style?: CSSProperties;
};

export type DialogProps = RootPrimitiveProps;

export function Dialog({
    children,
    open,
    defaultOpen = false,
    onOpenChange,
    ...props
}: DialogProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const [isOpen, setOpen] = useControllableState({
        value: open,
        defaultValue: defaultOpen,
        onChange: onOpenChange,
    });
    const RootPrimitive: ComponentType<RootPrimitiveProps> = isDesktop
        ? DialogPrimitive.Root
        : DrawerPrimitive.Root;

    return (
        <RootPrimitive {...props} open={isOpen} onOpenChange={setOpen}>
            {children}
        </RootPrimitive>
    );
}

export type DialogTriggerProps = ComponentPropsWithRef<'button'> & ButtonVariantProps;

export function DialogTrigger({ ...props }: DialogTriggerProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const TriggerPrimitive: ComponentType<RenderedButtonPrimitiveProps> = isDesktop
        ? DialogPrimitive.Trigger
        : DrawerPrimitive.Trigger;

    return <TriggerPrimitive data-slot="dialog-trigger" render={<Button {...props} />} />;
}

export type DialogPortalProps = DialogPrimitive.Portal.Props;

export function DialogPortal({ children, ...props }: DialogPortalProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const PortalPrimitive: ComponentType<DialogPrimitive.Portal.Props> = isDesktop
        ? DialogPrimitive.Portal
        : DrawerPrimitive.Portal;

    return (
        <PortalPrimitive data-slot="dialog-portal" {...props}>
            {children}
        </PortalPrimitive>
    );
}

export type DialogOverlayProps = BackdropPrimitiveProps;

export function DialogOverlay({ className, ...props }: DialogOverlayProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const BackdropPrimitive: ComponentType<BackdropPrimitiveProps> = isDesktop
        ? DialogPrimitive.Backdrop
        : DrawerPrimitive.Backdrop;

    return (
        <BackdropPrimitive
            data-slot="dialog-overlay"
            className={cn(
                'fixed inset-0 z-50 bg-scrim/40',
                'transition-opacity ease-standard duration-(--duration-interactive)',
                'data-ending-style:duration-(--duration-state)',
                'data-starting-style:opacity-0 data-ending-style:opacity-0',
                className,
            )}
            {...props}
        />
    );
}

const dialogContentVariants = cva(
    [
        'pointer-events-auto relative z-10 flex w-full min-h-0 flex-col overflow-hidden',
        'sm:h-auto sm:max-h-full sm:max-w-[40rem] sm:rounded-2xl sm:border sm:border-line-weak',
        'bg-container text-content shadow-2xl outline-none',
        'transition-[transform,scale,opacity] ease-standard duration-(--duration-interactive)',
        'data-ending-style:duration-(--duration-state)',
        'data-starting-style:opacity-0 data-ending-style:opacity-0',
        'max-sm:[transform:translateY(max(0px,var(--drawer-swipe-movement-y,0px)))]',
        'max-sm:data-swiping:[transform:translateY(max(0px,var(--drawer-swipe-movement-y,0px)))]!',
        'max-sm:data-starting-style:[transform:translateY(1.5rem)]',
        'max-sm:data-ending-style:[transform:translateY(calc(max(0px,var(--drawer-swipe-movement-y,0px))_+_1.5rem))]',
        'sm:data-starting-style:scale-96 sm:data-ending-style:scale-96',
    ],
    {
        variants: {
            snapMode: {
                contain: 'max-h-[calc(100%-2rem)] rounded-t-2xl border-x border-t border-line-weak',
                max: 'h-[calc(100%-2rem)] rounded-t-2xl border-x border-t border-line-weak',
                fullscreen: 'h-full',
            },
        },
        defaultVariants: {
            snapMode: 'contain',
        },
    },
);

export type DialogContentVariantProps = VariantProps<typeof dialogContentVariants>;

export type DialogSnapMode = NonNullable<DialogContentVariantProps['snapMode']>;

export type DialogContentProps = PopupPrimitiveProps & DialogContentVariantProps;

export function DialogContent({
    className,
    children,
    ref,
    snapMode,
    ...props
}: DialogContentProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const cardRef = useRef<HTMLDivElement>(null);
    const popupRef = useMergedRefs(ref, cardRef);
    const ViewportPrimitive: ComponentType<DialogPrimitive.Viewport.Props> = isDesktop
        ? DialogPrimitive.Viewport
        : DrawerPrimitive.Viewport;
    const PopupPrimitive: ComponentType<PopupPrimitiveProps> = isDesktop
        ? DialogPrimitive.Popup
        : DrawerPrimitive.Popup;

    return (
        <DialogPortal>
            <DialogOverlay />
            <ViewportPrimitive className="pointer-events-none fixed inset-0 z-50 flex flex-col justify-end sm:flex-row sm:items-start sm:justify-center sm:p-8">
                <Container
                    level={1}
                    render={
                        <PopupPrimitive
                            data-slot="dialog-content"
                            ref={popupRef}
                            initialFocus={cardRef}
                            className={cn(dialogContentVariants({ snapMode }), className)}
                            {...props}
                        />
                    }
                >
                    {children}
                </Container>
            </ViewportPrimitive>
        </DialogPortal>
    );
}

export type DialogHeaderProps = ComponentPropsWithRef<'div'>;

export function DialogHeader({ className, ...props }: DialogHeaderProps): ReactElement {
    return (
        <Container
            data-slot="dialog-header"
            className={cn(
                'relative z-10 flex shrink-0 items-center gap-default px-5 py-relaxed sm:px-6',
                'border-b',
                className,
            )}
            {...props}
        />
    );
}

export type DialogBodyProps = ComponentPropsWithRef<'div'>;

export function DialogBody({ className, ...props }: DialogBodyProps): ReactElement {
    return (
        <div
            data-slot="dialog-body"
            className={cn('min-h-0 flex-1 overflow-auto overscroll-contain px-5 py-5 sm:px-6', className)}
            {...props}
        />
    );
}

export type DialogFooterProps = ComponentPropsWithRef<'div'>;

export function DialogFooter({ className, ...props }: DialogFooterProps): ReactElement {
    return (
        <div
            data-slot="dialog-footer"
            className={cn(
                'relative z-10 flex shrink-0 flex-row justify-end gap-cozy px-5 py-relaxed sm:px-6',
                'border-t',
                className,
            )}
            {...props}
        />
    );
}

export type DialogTitleProps = ComponentPropsWithRef<'h2'>;

export function DialogTitle({ className, ...props }: DialogTitleProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const TitlePrimitive: ComponentType<TitlePrimitiveProps> = isDesktop
        ? DialogPrimitive.Title
        : DrawerPrimitive.Title;

    return (
        <TitlePrimitive
            data-slot="dialog-title"
            className={cn('min-w-0 flex-1 truncate text-heading-2', className)}
            {...props}
        />
    );
}

export type DialogCloseProps = ComponentPropsWithRef<'button'> & ButtonVariantProps;

export function DialogClose({ className, children, ...props }: DialogCloseProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const ClosePrimitive: ComponentType<RenderedButtonPrimitiveProps> = isDesktop
        ? DialogPrimitive.Close
        : DrawerPrimitive.Close;

    return (
        <ClosePrimitive
            data-slot="dialog-close"
            render={
                <Button className={className} {...props}>
                    {children}
                </Button>
            }
        />
    );
}

export type DialogIconCloseProps = DialogCloseProps;

export function DialogIconClose({ className, children, ...props }: DialogIconCloseProps): ReactElement {
    const isDesktop = useBreakpoint('sm');
    const ClosePrimitive: ComponentType<RenderedButtonPrimitiveProps> = isDesktop
        ? DialogPrimitive.Close
        : DrawerPrimitive.Close;

    return (
        <ClosePrimitive
            data-slot="dialog-close"
            render={
                <IconButton label="Close" className={className} {...props}>
                    {children ?? <XIcon className="size-5" />}
                </IconButton>
            }
        />
    );
}
